import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { JwtService } from 'src/app/jwt.service';
import { ItemManager } from 'src/app/managers/item-manager';
import { SnackbarManager } from 'src/app/managers/snackbar-manager';
import { InfoVeiculo } from 'src/app/persistences/info-veiculo';
import { ItemDetalhado } from 'src/app/persistences/item-detalhado';
import { ItemSimplesView } from 'src/app/persistences/item-simples-view';
import { ConfigService } from 'src/app/services/config.service';
import { ItemService } from 'src/app/services/item.service';
import { CartSingletonService } from 'src/app/singletons/cart-singleton.service';
import { NumberUtils } from 'src/app/utils/utils/number-utils';
import { DialogSimilaresComponent } from '../dialog-similares/dialog-similares.component';
import { ItemDetalhadoComponent } from '../item-detalhado/item-detalhado.component';
import { ListaPrecoComponent } from '../lista-preco/lista-preco.component';
import { QuantidadeListaComponent } from '../quantidade-lista/quantidade-lista.component';

export interface AplicacaoVeiculo {
  complemento: string;
  linhas: string[];
}

export interface CodigoConversaoTabela {
  codigo: string;
  descricao: string;
  titulo: string;
}

/** Tempo de espera antes de buscar os detalhes da linha expandida (evita requisições na navegação rápida). */
const ATRASO_CARGA_DETALHES_MS = 200;

/** Máximo de linhas de aplicação exibidas na área expandida; o restante é indicado e visto no item detalhado. */
const LIMITE_LINHAS_APLICACAO = 6;

@Component({
  standalone: false,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'tbody[app-linha-tabela-item]',
  templateUrl: './linha-tabela-item.component.html',
  styleUrls: ['./linha-tabela-item.component.css'],
})
export class LinhaTabelaItemComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  public itemPesquisa: ItemSimplesView;
  @Input()
  public selecionado = false;
  @Input()
  public expandido = false;

  @Output()
  public selecionar = new EventEmitter<void>();
  @Output()
  public alternarExpansao = new EventEmitter<void>();

  @ViewChild('qtd')
  private qtd: QuantidadeListaComponent;

  public aplicacao: AplicacaoVeiculo[] = [];
  public conversoes: CodigoConversaoTabela[] = [];
  public qtdSimilares: number | null = null;
  public carregandoDetalhes = false;

  private detalhesCarregados = false;
  private timerDetalhes: ReturnType<typeof setTimeout>;
  private destroy$ = new Subject<void>();

  constructor(
    public elemento: ElementRef<HTMLElement>,
    private dialog: MatDialog,
    private itemManager: ItemManager,
    private itemService: ItemService,
    private cartSingletonService: CartSingletonService,
    private configService: ConfigService,
    private jwtService: JwtService,
    private snackBarManager: SnackbarManager
  ) {}

  get item(): ItemDetalhado {
    return this.itemPesquisa.itemDetalhado;
  }

  ngOnInit(): void {
    this.sincronizarComCarrinho();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['itemPesquisa'] && !changes['itemPesquisa'].firstChange) {
      this.sincronizarComCarrinho();
    }
    if (changes['expandido']) {
      clearTimeout(this.timerDetalhes);
      if (this.expandido && !this.detalhesCarregados) {
        this.timerDetalhes = setTimeout(() => this.carregarDetalhes(), ATRASO_CARGA_DETALHES_MS);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    clearTimeout(this.timerDetalhes);
  }

  /** Clique em qualquer área da linha (exceto botões/campos) expande/recolhe o item. */
  aoClicarNaLinha(evento: MouseEvent): void {
    const alvo = evento.target as HTMLElement | null;
    if (alvo?.closest('button, input, a, mat-checkbox, [role="button"]')) {
      this.selecionar.emit();
      return;
    }
    this.alternarExpansao.emit();
  }

  // ---- Valores exibidos (mesmas regras da visualização Lista) ----

  get exibeDesconto(): boolean {
    return this.configService.getConfig().getExibeDesconto;
  }

  /**
   * Mesmo critério da Lista e dos Cards (`getQtAtual` em lista-itens/card-lista-itens): estoque físico (`qtAtual`).
   * A indisponibilidade para venda continua sinalizada por "Consulte..." e pelo botão "Indisponível".
   */
  get temEstoque(): boolean {
    return this.item.itemDetalhes == undefined ? false : this.item.itemDetalhes.qtAtual > 0;
  }

  get temSimilares(): boolean {
    return this.item.itemDetalhes?.temSimilares === true;
  }

  get temRelacionados(): boolean {
    return this.item.itemDetalhes?.temRelacionados === true;
  }

  /** Aplicação limitada a LIMITE_LINHAS_APLICACAO linhas de veículo, para a área expandida caber na tela. */
  get aplicacaoVisivel(): AplicacaoVeiculo[] {
    let restante = LIMITE_LINHAS_APLICACAO;
    const visiveis: AplicacaoVeiculo[] = [];
    for (const grupo of this.aplicacao) {
      if (restante <= 0) {
        break;
      }
      const linhas = grupo.linhas.slice(0, restante);
      restante -= linhas.length;
      visiveis.push({ complemento: grupo.complemento, linhas });
    }
    return visiveis;
  }

  /** Quantidade de linhas de aplicação não exibidas por causa do limite. */
  get aplicacaoOcultas(): number {
    const total = this.aplicacao.reduce((soma, grupo) => soma + grupo.linhas.length, 0);
    return Math.max(0, total - LIMITE_LINHAS_APLICACAO);
  }

  get fichaTecnica(): string {
    return this.item.itemDetalhes?.fichaTecnica ?? '';
  }

  get precoDisponivel(): boolean {
    return this.item.itemDetalhes.qtDisponivel > 0 && !(this.itemPesquisa.preco <= 0.01);
  }

  get temDesconto(): boolean {
    return this.itemPesquisa.pcDesconto > 0 && this.exibeDesconto && this.precoDisponivel;
  }

  /** Quantidade do carrinho quando o item já está nele; senão, a quantidade em pré-visualização. */
  get quantidade(): number {
    const doCarrinho = this.itemDoCarrinho();
    if (doCarrinho && doCarrinho.itemPedido.quantidade > 0) {
      return doCarrinho.itemPedido.quantidade;
    }
    return this.item.itemPedido?.quantidade ?? 0;
  }

  get quantidadeString(): string {
    return this.quantidade.toLocaleString('pt-BR', { useGrouping: false });
  }

  get valorUnitario(): number {
    return this.item.itemPedido.valorLiquidoIpiSt ?? 0;
  }

  get valorTotal(): number {
    return NumberUtils.roundTo(this.valorUnitario * this.quantidade, 2);
  }

  get imagem(): string {
    return this.itemPesquisa.imagem ? this.itemPesquisa.imagem : 'assets/images/no_image.png';
  }

  get outrosPrecosIndisponivel(): boolean {
    // Chamado #74296: item com unidade de venda bloqueada fica sem tabela de preço; só pode ser excluído.
    if (this.itemManager.isUnidadeVendaIndisponivel(this.item)) {
      return true;
    }
    const result = this.itemManager.removerTabelasDePrecoDuplicas(this.item);
    return result === null || result.length <= 1;
  }

  // ---- Ações disparadas pelo teclado (delegam ao componente de quantidade) ----

  incrementar(): void {
    this.qtd?.incrementarPorTeclado();
  }

  decrementar(): void {
    this.qtd?.decrementarPorTeclado();
  }

  adicionar(): void {
    this.qtd?.adicionarPorTeclado();
  }

  // ---- Diálogos (mesmo comportamento das visualizações Lista/Cards) ----

  /** Abre a lista de itens similares (mesma sessão do item detalhado); cada similar abre no item detalhado. */
  abrirSimilares(tipo: 'similares' | 'relacionados' = 'similares'): void {
    this.sincronizarComCarrinho();
    const tabelaAtiva = this.item.itemDetalhes.outrosPrecos?.find((p) => p.idGeral === this.item.itemPedido.idTabelaPreco);
    const dialogRef = this.dialog.open(DialogSimilaresComponent, {
      width: '95%',
      maxWidth: '1000px',
      autoFocus: false, // evita que o botão X abra com o anel de foco
      data: {
        idItem: this.item.itemDetalhes.idItem,
        cdItem: this.item.itemDetalhes.cdItem,
        cdTabelaPrecoAtiva: tabelaAtiva?.cdTabelaPreco,
        unMedidaAtiva: tabelaAtiva?.unMedida,
        tipo,
      },
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.itemManager.getValor(this.item).then();
    });
  }

  openDialogDetalhes(): void {
    this.sincronizarComCarrinho();
    const dictItem: Map<number, ItemDetalhado> = new Map();
    dictItem.set(this.itemPesquisa.idItem, new ItemDetalhado());
    const idItem = this.item.itemDetalhes.idItem;
    const dialogRef = this.dialog.open(ItemDetalhadoComponent, {
      width: '95%',
      maxWidth: '80vw',
      height: '95%',
      data: {
        trilhaItens: dictItem,
        itemDetalhado: this.item,
        id: idItem,
      },
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(
      () => {
        this.item.itemDetalhes.idItem = idItem;
        this.itemManager.getValor(this.item).then();
      },
      (error) => {
        this.jwtService.validarAcessToken(error);
        this.snackBarManager.openSnackBarErro(error, 'Não foi possível concluir a operação com o item.', 'Ok', 3000);
      }
    );
  }

  listarPrecosDoItem(): void {
    // Chamado #74296: item com unidade de venda bloqueada fica sem tabela de preço; só pode ser excluído.
    if (this.itemManager.isUnidadeVendaIndisponivel(this.item)) {
      return;
    }
    const dialogRef = this.dialog.open(ListaPrecoComponent, {
      width: 'auto',
      height: 'auto',
      data: {
        itemDetalhado: this.item,
      },
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(
      (tabelaPreco) => {
        if (tabelaPreco !== null) {
          this.itemManager.selecionarTabelaDePrecoDoItem(tabelaPreco, this.item);
          this.itemManager.getValor(this.item).then(() => {
            this.itemPesquisa.idTabelaPrecoProduto = this.item.itemPedido.idTabelaPreco;
            this.itemManager.atualizarTabelaDePrecoDoItem(this.item);
          });
        }
      },
      (error) => {
        this.jwtService.validarAcessToken(error);
        this.snackBarManager.openSnackBarErro(error, 'Não foi possível carregar a tabela de preços do item.', 'Ok', 3000);
      }
    );
  }

  // ---- Internos ----

  private itemDoCarrinho(): ItemDetalhado | undefined {
    return this.cartSingletonService.getCarrinhoEstatico().getItensCarrinho.find(
      (itemCart) => itemCart.itemDetalhes.idItem == this.itemPesquisa.idItem
    );
  }

  /** Mesmo ajuste feito por ItemPesquisaComponent: o item da lista assume os dados do item que já está no carrinho. */
  private async sincronizarComCarrinho(): Promise<void> {
    const doCarrinho = this.itemDoCarrinho();
    if (doCarrinho === undefined) {
      return;
    }
    const pedido = this.item.itemPedido;
    pedido.idTabelaPreco = doCarrinho.itemPedido.idTabelaPreco;
    pedido.unMedVenda = doCarrinho.itemPedido.unMedVenda;
    pedido.valorLiquido = doCarrinho.itemPedido.valorLiquido;
    pedido.valorLiquidoIpiSt = doCarrinho.itemPedido.valorLiquidoIpiSt;
    pedido.total = NumberUtils.roundTo(doCarrinho.itemPedido.total, 2);
    pedido.quantidade = doCarrinho.itemPedido.quantidade;
    await this.itemManager.getValor(this.item);
  }

  /** Busca aplicação (veículos) e conversões/similares uma única vez, na primeira expansão da linha. */
  private carregarDetalhes(): void {
    if (this.detalhesCarregados || !this.expandido) {
      return;
    }
    this.detalhesCarregados = true;
    this.carregandoDetalhes = true;
    const idItem = this.itemPesquisa.idItem;
    let pendentes = 2;
    const concluir = () => {
      pendentes--;
      if (pendentes === 0) {
        this.carregandoDetalhes = false;
      }
    };

    this.itemService.getInfoVeiculo(idItem).pipe(takeUntil(this.destroy$)).subscribe(
      (veiculos: InfoVeiculo[]) => {
        this.item.itemDetalhes.infoVeiculo = veiculos;
        this.aplicacao = this.montarAplicacao(veiculos);
        concluir();
      },
      (error) => {
        this.detalhesCarregados = false;
        concluir();
        this.jwtService.validarAcessToken(error);
        this.snackBarManager.openSnackBarErro(error, 'Não é possível consultar as informações do veículo.', 'Ok', 3000);
      }
    );

    this.itemService.getSimilares(idItem).pipe(takeUntil(this.destroy$)).subscribe(
      (res) => {
        if (res != undefined) {
          this.item.itemDetalhes.cdsConversao = res['codigos'];
          this.conversoes = this.montarConversoes(res['codigos'], res['itens']);
          this.qtdSimilares = (res['itens'] ?? []).length;
        }
        concluir();
      },
      (error) => {
        this.detalhesCarregados = false;
        concluir();
        this.jwtService.validarAcessToken(error);
        this.snackBarManager.openSnackBarErro(error, 'Não é possível consultar os itens similares.', 'Ok', 3000);
      }
    );
  }

  private montarAplicacao(veiculos: InfoVeiculo[]): AplicacaoVeiculo[] {
    const grupos = new Map<string, string[]>();
    (veiculos ?? []).forEach((v) => {
      const complemento = (v.complemento ?? '').trim().toUpperCase();
      const anos = [v.anoInicio, v.anoFim]
        .filter((ano) => ano !== null && ano !== undefined && String(ano).trim() !== '')
        .map((ano) => String(ano).trim().slice(-2))
        .join('/');
      const linha = `${(v.modelo ?? '').trim()} ${anos}`.trim().toUpperCase();
      if (!grupos.has(complemento)) {
        grupos.set(complemento, []);
      }
      if (linha && !grupos.get(complemento).includes(linha)) {
        grupos.get(complemento).push(linha);
      }
    });
    return Array.from(grupos, ([complemento, linhas]) => ({ complemento, linhas }));
  }

  /** Item atual + itens similares + códigos de conversão cadastrados. */
  private montarConversoes(codigos: any[], similares: any[]): CodigoConversaoTabela[] {
    const lista: CodigoConversaoTabela[] = [];
    const atual = this.item.itemDetalhes;
    lista.push({
      codigo: atual.cdItem,
      descricao: atual.nmReduzido || atual.nmItem,
      titulo: 'Item atual',
    });
    (similares ?? []).forEach((s) => {
      lista.push({ codigo: s['cdItem'], descricao: s['nmItem'], titulo: 'Item similar' });
    });
    (codigos ?? []).forEach((c) => {
      lista.push({ codigo: c['codigo'], descricao: c['descricao'], titulo: c['tipoCodigo'] ?? 'Conversão' });
    });
    return lista;
  }
}
